import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { AgruparRelatorio } from '../../../enums/AgruparRelatorio.enum';
import { FiltroOrdenacaoRelatorio } from '../../../enums/FiltroOrdenacaoRelatorio.enum';
import { Moeda } from '../../../enums/moeda.enum';
import { environment } from '../../../environments/environment';
import { PessoaDTO } from '../../../models/clientes/pessoa.model';
import { MoedaDTO } from '../../../models/configuracoes/empresa.model';
import { PeriodoFiltro } from '../../../models/dashboard/periodo-filtro.model';
import { GrupoDTO } from '../../../models/grupo.model';
import { MarcaDTO } from '../../../models/marca.model';
import { NotaDTO } from '../../../models/nota.model';
import { ProdutosDTO } from '../../../models/produto.models';
import { RelatorioItensVendidosParams } from '../../../models/relatorios/relatorio-itens-vendidos.model';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { NotaService } from '../../../services/financeiro/notas.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { ProdutosService } from '../../../services/produtos/produto.service';
import { RelatorioItensVendidosService } from '../../../services/relatorios/relatorio-itens-vendidos.service';
import { calcularPeriodoPadraoRelatorio } from '../../../shared/utils/periodo-relatorio-padrao.util';
import { PRESETS_PERIODO_RELATORIO } from '../../../shared/utils/periodo.utils';
import { SafeStorageService } from '../../../shared/safe-storage.service';
import { FileUtils } from '../../../shared/utils/file-utils';

@Component({
  selector: 'app-relatorio-itens-vendidos',
  templateUrl: './relatorio-itens-vendidos.component.html',
  styleUrls: ['./relatorio-itens-vendidos.component.scss']
})
export class RelatorioItensVendidosComponent implements OnInit, OnDestroy {
  env = environment;

  // Texto exibido no botao do filtro de periodo (app-periodo-filtro, modo avancado).
  periodoTexto = '';

  // Presets do filtro de periodo: lista padrao + "Mostrar todos" (so relatorios).
  readonly opcoesPeriodo = PRESETS_PERIODO_RELATORIO;

  idEmpresa: string = '';
  searchNome: string = '';
  totalRecords: number = 0;
  first3: number = 0;


  // Parâmetros do endpoint
  params: RelatorioItensVendidosParams = {
    itemId: undefined,
    marcaId: undefined,
    grupoId: undefined,
    subGrupoId: undefined,
    pessoaId: undefined,
    fornecedorId: undefined,
    vendedorId: undefined,
    notaId: undefined,
    dataHoraInicial: '',
    dataHoraFinal: '',
    agrupar: 'ITEM', // valor padrão agora é ITEM
    filtroOrdenacao: undefined
  };

  // Listas para popular selects
  produtos: any[] = [];
  selectedProduto: ProdutosDTO | null = null;
  loadingProdutos: boolean = false;

  marcas: any[] = [];
  selectedMarca: MarcaDTO | null = null;
  loadingMarcas: boolean = false;

  grupos: any[] = [];
  selectedGrupo: GrupoDTO | null = null;
  loadingGrupos: boolean = false;

  subgrupos: any[] = [];
  selectedSubGrupo: GrupoDTO | null = null;
  loadingSubGrupos: boolean = false;

  clientes: any[] = [];
  selectedCliente: PessoaDTO | null = null;
  loadingClientes: boolean = false;

  // Lista para popular o select de fornecedor
  fornecedores: any[] = [];
  selectedFornecedor: PessoaDTO | null = null;
  loadingFornecedores: boolean = false;

  // Lista para popular o select de vendedor
  vendedores: any[] = [];
  selectedVendedor: PessoaDTO | null = null;
  loadingVendedores = false;

  notas: any[] = [];
  selectedNota: NotaDTO | null = null;
  loadingNotas: boolean = false;

  moedaPadrao: MoedaDTO | undefined;

  emitindoRelatorio: boolean = false;

  // Opções para agrupar
  agruparOptions: { value: string; label: string }[] = [];

  // Opções para ordenação
  ordenacaoOptions: { value: string; label: string }[] = [];
  selectedOrdenacao: string | null = null;

  constructor(
    private safe: SafeStorageService,
    private translate: TranslateService,
    private messageService: MessageService,
    private relatorioService: RelatorioItensVendidosService,
    private produtoService: ProdutosService,
    private pessoaService: PessoaService,
    private notaService: NotaService,
    private empresaConfigService: EmpresaConfigService
  ) { }

  private langChangeSub!: Subscription;

  ngOnInit(): void {
    // Obtém o idEmpresa do sessionStorage
    this.idEmpresa = this.safe.getItem('id') || '';
    this.inicializarPeriodoPadrao();
    this.buildAgruparOptions();
    this.buildOrdenacaoOptions();
    this.findProdutosPorFiltros();
    this.findClientes();
    this.findFornecedores();
    this.findVendedores();
    this.findNotas();

    // Atualiza labels (agrupamento/ordenacao) quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.buildAgruparOptions();
      this.buildOrdenacaoOptions();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // Inicializa o periodo padrao "Hoje" para que os params tenham datas
  // antes de o usuario abrir o dialog do filtro. O app-periodo-filtro nao
  // emite no init, entao replicamos o que o dashboard faz.
  private inicializarPeriodoPadrao(): void {
    // Alinhado ao [valorPadrao]="'today'" do app-periodo-filtro.
    this.aplicarPeriodo(calcularPeriodoPadraoRelatorio(this.translate, 'today'));
  }

  // Handler do (periodoChanged) do app-periodo-filtro.
  onPeriodoChanged(periodo: PeriodoFiltro): void {
    this.aplicarPeriodo(periodo);
  }

  private aplicarPeriodo(periodo: PeriodoFiltro): void {
    this.params.dataHoraInicial = periodo.dataInicio;
    this.params.dataHoraFinal = periodo.dataFim;
    this.periodoTexto = periodo.texto;
  }

  private buildAgruparOptions() {
    this.agruparOptions = Object.values(AgruparRelatorio).map(agrupar => ({
      value: agrupar,
      label: this.translate.instant(`agrupar_relatorio.${agrupar}`)
    }));
  }

  private buildOrdenacaoOptions() {
    this.ordenacaoOptions = Object.values(FiltroOrdenacaoRelatorio).map(ordenacao => ({
      value: ordenacao,
      label: this.translate.instant(`ordenacao_relatorio.${ordenacao}`)
    }));
  }

  findNotas(
    page: number = 0,
    size: number = 10,
    sort: string = 'id,asc',
  ) {
    let filter = '';
    filter += `codigoNota=${this.searchNome}`;

    this.notaService.get(
      '/nota/buscar', {
      page,
      size,
      sort,
      filter: `tipoDeTransacao=VENDA${filter ? `&${filter}` : ''}`
    }).subscribe({
      next: (res) => {
        this.notas = res.content;
      },
      error: (err) => console.error(err),
    });
  }

  onNotasFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingNotas = true;
    this.findNotas();
    this.loadingNotas = false;
  }

  findProdutosPorFiltros(
    page: number = 0,
    size: number = 10,
    sort: string = 'id,asc',
  ) {
    let filter = '&saldo=TODOS';

    if (!isNaN(Number(this.searchNome)) && this.searchNome.trim()) {
      filter += `&codigoItem=${this.searchNome}`;
    } else if (this.searchNome.trim()) {
      filter += `&nome=${this.searchNome}`;
      filter += `&nomeGrupo=${this.searchNome}`;
      filter += `&nomeSubGrupo=${this.searchNome}`;
      filter += `&nomeMarca=${this.searchNome}`;
      filter += `&barras=${this.searchNome}`;
    }

    this.produtoService
      .get('/item/buscarPorFiltros', {
        page,
        size,
        sort,
        filter,
      })
      .subscribe(
        (response) => {
          this.produtos = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
        },
        (error) => {
          console.error('Erro ao buscar produtos:', error);
          this.produtos = [];
          this.totalRecords = 0;
        }
      );
  }

  onProdutosFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingProdutos = true;
    this.findProdutosPorFiltros();
    this.loadingProdutos = false;
  }

  findClientes(
    page: number = 0,
    size: number = 10,
    sort: string = 'id,asc',
  ) {
    let filter = `categoriaCliente=true`;

    if (this.searchNome && this.searchNome.trim() !== '') {
      const search = encodeURIComponent(this.searchNome.trim());

      filter += `&email=${search}`;
      filter += `&nome=${search}`;
      filter += `&fantasia=${search}`;
      filter += `&ruc=${search}`;
      filter += `&telefone=${search}`;
      filter += `&ci=${search}`;
      filter += `&nomeCidade=${search}`;

      if (!isNaN(Number(search))) {
        filter += `&codigoPessoa=${search}`;
      }
    }

    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: filter,
      }).subscribe(
        (response) => {
          this.clientes = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
        },
        (error) => {
          console.error('Erro ao buscar clientes:', error);
          this.clientes = [];
          this.totalRecords = 0;
        }
      );
  }

  onClientesFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingClientes = true;
    this.findClientes();
    this.loadingClientes = false;
  }

  findFornecedores(page: number = 0, sort: string = 'id,asc') {
    const size = 10;
    let filter = '';

    filter += `&email=${this.searchNome}`;
    filter += `&nome=${this.searchNome}`;
    filter += `&fantasia=${this.searchNome}`;
    filter += `&ruc=${this.searchNome}`;
    filter += `&telefone=${this.searchNome}`;
    filter += `&ci=${this.searchNome}`;
    filter += `&nomeCidade=${this.searchNome}`;

    if (!isNaN(Number(this.searchNome))) {
      filter += `&codigoPessoa=${this.searchNome}`;
    }
    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: `categoriaFornecedor=true${filter ? `&${filter}` : ''}`,
      })
      .subscribe(
        (response: any) => {
          this.fornecedores = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
        },
        (error: any) => {
          console.error('Erro ao buscar fornecedores:', error);
          this.fornecedores = [];
          this.totalRecords = 0;
        }
      );
  }

  onFornecedorFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingFornecedores = true;
    this.findFornecedores();
    this.loadingFornecedores = false;
  }

  findVendedores(page: number = 0, sort: string = 'id,asc') {
    const size = 10;
    let filter = '';

    filter += `&email=${this.searchNome}`;
    filter += `&nome=${this.searchNome}`;
    filter += `&fantasia=${this.searchNome}`;
    filter += `&ruc=${this.searchNome}`;
    filter += `&telefone=${this.searchNome}`;
    filter += `&ci=${this.searchNome}`;
    filter += `&nomeCidade=${this.searchNome}`;

    if (!isNaN(Number(this.searchNome))) {
      filter += `&codigoPessoa=${this.searchNome}`;
    }
    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: `categoriaVendedor=true${filter ? `&${filter}` : ''}`,
      })
      .subscribe(
        (response: any) => {
          this.vendedores = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
        },
        (error: any) => {
          console.error('Erro ao buscar vendedores:', error);
          this.vendedores = [];
          this.totalRecords = 0;
        }
      );
  }

  onVendedorFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingVendedores = true;
    this.findVendedores();
    this.loadingVendedores = false;
  }

  carregarMoedaPadrao(): void {
    if (!this.idEmpresa) {
      console.error("ID da empresa não encontrado no sessionStorage.");
      return;
    }

    this.empresaConfigService.get(`/configuracaoEmpresa/${this.idEmpresa}`).subscribe({
      next: (res) => {
        const moeda = res?.moeda;
        this.moedaPadrao = moeda;
      },
      error: (err) => {
        console.error('Erro ao buscar configuração da empresa:', err);
      },
    });
  }

  // Botão "Emitir Relatório"
  emitirRelatorio(): void {
    this.emitindoRelatorio = true;

    // As datas ja foram aplicadas em params via onPeriodoChanged / periodo padrao.
    // Adiciona parâmetros opcionais apenas se preenchidos
    if (this.selectedNota?.id) {
      this.params.notaId = this.selectedNota.id;
    }
    if (this.selectedProduto?.id) {
      this.params.itemId = this.selectedProduto.id;
    }
    if (this.selectedMarca?.id) {
      this.params.marcaId = this.selectedMarca.id;
    }
    if (this.selectedCliente?.id) {
      this.params.pessoaId = this.selectedCliente.id;
    }
    if (this.selectedGrupo?.id) {
      this.params.grupoId = this.selectedGrupo.id;
    }
    if (this.selectedSubGrupo?.id) {
      this.params.subGrupoId = this.selectedSubGrupo.id;
    }
    if (this.selectedFornecedor?.id) {
      this.params.fornecedorId = this.selectedFornecedor.id;
    }
    if (this.selectedVendedor?.id) {
      this.params.vendedorId = this.selectedVendedor.id;
    }
    if (this.selectedOrdenacao) {
      this.params.filtroOrdenacao = this.selectedOrdenacao;
    }

    this.relatorioService.emitirRelatorio(this.params).subscribe({
      next: (base64Pdf: string) => {
        // this.abrirPdf(base64Pdf);
        FileUtils.abrirPdf(base64Pdf, 'relatorio_itens_vendidos.pdf');
        this.emitindoRelatorio = false;
      },
      error: (err) => {
        console.error('Erro ao gerar relatório', err);
        this.emitindoRelatorio = false;
        if (err?.status === 404) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.aviso'),
            detail: this.translate.instant('relatorios.mensagens.nenhum_dado_encontrado'),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: this.translate.instant('relatorios.mensagens.erro_gerar_relatorio'),
          });
        }
      },
    });
  }

  // Formata datas
  private formatDate(date: Date | null): string | undefined {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/images/profile_placeholder.png';
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  private obterNomeMoeda(moeda: any): string | null {
    return moeda?.enum ?? null;
  }

  formatarValorMonetario(valor: number, moeda?: MoedaDTO): string {
    if (valor == null || isNaN(valor)) return '-';

    if (!moeda) {
      moeda = this.moedaPadrao;
    }

    const configuracoesMoeda: Record<Moeda, { locale: string; simbolo: string }> = {
      [Moeda.GUARANI]: { locale: 'es-PY', simbolo: 'Gs' },
      [Moeda.REAL]: { locale: 'pt-BR', simbolo: 'R$' },
      [Moeda.DOLAR]: { locale: 'en-US', simbolo: '$' },
      [Moeda.PESO]: { locale: 'es-AR', simbolo: '$' },
    };

    const nomeMoeda = this.obterNomeMoeda(moeda) as Moeda;
    const { locale, simbolo } = configuracoesMoeda[nomeMoeda] ?? configuracoesMoeda[Moeda.GUARANI];

    const isGuarani = nomeMoeda === Moeda.GUARANI;

    const options: Intl.NumberFormatOptions = isGuarani
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

    const valorFormatado = new Intl.NumberFormat(locale, options).format(valor);

    return `${simbolo} ${valorFormatado}`;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.key === 'Enter' && !this.emitindoRelatorio) {
      event.preventDefault();
      this.emitirRelatorio();
    }
  }
}
